import React, { useEffect, useState, useCallback } from 'react';
import {
  ShieldCheck, Plus, Trash2, ChevronRight, Check, Loader2, AlertCircle, Lock,
} from 'lucide-react';
import { useFirm } from '../contexts/FirmContext';
import { usePermissions, AccessDenied } from '../contexts/PermissionsContext';
import {
  listFirmRoles, createFirmRole, deleteFirmRole,
  listRolePermissions, addRolePermission, removeRolePermission,
  seedDefaultRoles,
} from '../lib/db';
import { PERMISSION_GROUPS } from '../lib/permissions';
import { PlanGate } from '../contexts/PlanContext';

const ROLE_COLORS = [
  '#EF4444', '#F97316', '#EAB308', '#22C55E',
  '#06B6D4', '#3B82F6', '#8B5CF6', '#EC4899',
  '#6B7280', '#0F172A',
];

// ── Permission toggle row ─────────────────────────────────────────────────────
function PermissionRow({ perm, checked, onChange, saving, isSystemAdmin }) {
  return (
    <label className={`flex items-center gap-3 p-2.5 rounded-lg cursor-pointer transition
      ${checked
        ? 'bg-indigo-50 dark:bg-indigo-900/20'
        : 'hover:bg-slate-50 dark:hover:bg-slate-800/50'}`}
    >
      <button
        type="button"
        onClick={() => !saving && onChange(perm.key)}
        disabled={saving || isSystemAdmin}
        className={`w-5 h-5 rounded flex items-center justify-center border-2 transition flex-shrink-0
          ${checked
            ? 'bg-indigo-600 border-indigo-600'
            : 'border-slate-300 dark:border-slate-600'}
          ${(saving || isSystemAdmin) ? 'opacity-50 cursor-not-allowed' : ''}`}
      >
        {checked && <Check size={11} className="text-white" />}
      </button>
      <span className="text-sm text-slate-700 dark:text-slate-300">{perm.label}</span>
      {saving && <Loader2 size={12} className="animate-spin text-slate-400 ml-auto" />}
    </label>
  );
}

// ── Create role modal ─────────────────────────────────────────────────────────
function CreateRoleModal({ onClose, onCreated, firmId }) {
  const [name, setName]           = useState('');
  const [description, setDesc]    = useState('');
  const [color, setColor]         = useState('#6366F1');
  const [saving, setSaving]       = useState(false);
  const [error, setError]         = useState('');

  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim()) { setError('Role name is required.'); return; }
    setSaving(true);
    const { data, error: err } = await createFirmRole(firmId, { name: name.trim(), description, color });
    if (err) { setError(err.message); setSaving(false); return; }
    onCreated(data);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40">
      <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-xl w-full max-w-md p-6">
        <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-4">Create new role</h3>
        <form onSubmit={submit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
              Role name <span className="text-red-500">*</span>
            </label>
            <input
              value={name} onChange={e => setName(e.target.value)}
              placeholder="e.g. Senior Associate"
              className="w-full px-3 py-2 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-900 dark:text-white text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
              Description
            </label>
            <input
              value={description} onChange={e => setDesc(e.target.value)}
              placeholder="Optional description"
              className="w-full px-3 py-2 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-900 dark:text-white text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
              Color
            </label>
            <div className="flex flex-wrap gap-2">
              {ROLE_COLORS.map(c => (
                <button
                  key={c} type="button"
                  onClick={() => setColor(c)}
                  className={`w-7 h-7 rounded-full border-2 transition ${color === c ? 'border-slate-900 dark:border-white scale-110' : 'border-transparent'}`}
                  style={{ background: c }}
                />
              ))}
            </div>
          </div>
          {error && (
            <p className="text-sm text-red-600 flex items-center gap-1.5">
              <AlertCircle size={14} /> {error}
            </p>
          )}
          <div className="flex gap-2 justify-end pt-2">
            <button type="button" onClick={onClose}
              className="px-4 py-2 text-sm rounded-xl border border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800 transition">
              Cancel
            </button>
            <button type="submit" disabled={saving}
              className="px-4 py-2 text-sm rounded-xl bg-indigo-600 hover:bg-indigo-700 text-white font-medium transition disabled:opacity-50">
              {saving ? 'Creating…' : 'Create role'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────
function RolesPageInner() {
  const { firmId }        = useFirm();
  const { can, loading: permLoading } = usePermissions();

  const [roles, setRoles]             = useState([]);
  const [selectedId, setSelectedId]   = useState(null);
  const [permissions, setPermissions] = useState(new Set());
  const [loadingRoles, setLoadingRoles] = useState(true);
  const [loadingPerms, setLoadingPerms] = useState(false);
  const [savingPerm, setSavingPerm]   = useState(null); // permission key being toggled
  const [showCreate, setShowCreate]   = useState(false);
  const [error, setError]             = useState('');
  const [seeding, setSeeding]         = useState(false);

  // Re-run the default-roles seed for firms that signed up before the
  // auto-seed trigger landed and ended up with an empty roles list.
  const handleSeedDefaults = async () => {
    if (!firmId) return;
    setSeeding(true);
    setError('');
    const { error: err } = await seedDefaultRoles(firmId);
    setSeeding(false);
    if (err) { setError(err.message || 'Could not seed default roles.'); return; }
    await loadRoles();
  };

  // ── Load roles ──────────────────────────────────────────────────────────────
  const loadRoles = useCallback(async () => {
    if (!firmId) return;
    setLoadingRoles(true);
    const { data, error: err } = await listFirmRoles(firmId);
    if (err) { setError(err.message); setLoadingRoles(false); return; }
    setRoles(data || []);
    setLoadingRoles(false);
    // Auto-select first role
    if (data?.length && !selectedId) setSelectedId(data[0].id);
  }, [firmId, selectedId]);

  useEffect(() => { loadRoles(); }, [loadRoles]);

  // ── Load permissions for selected role ─────────────────────────────────────
  useEffect(() => {
    if (!selectedId) return;
    setLoadingPerms(true);
    listRolePermissions(selectedId).then(({ data }) => {
      setPermissions(new Set((data || []).map(p => p.permission)));
      setLoadingPerms(false);
    });
  }, [selectedId]);

  // ── Toggle a permission ────────────────────────────────────────────────────
  const togglePermission = async (permKey) => {
    if (savingPerm) return;
    setSavingPerm(permKey);
    const has = permissions.has(permKey);
    // Optimistic update
    setPermissions(prev => {
      const next = new Set(prev);
      has ? next.delete(permKey) : next.add(permKey);
      return next;
    });
    const { error: err } = has
      ? await removeRolePermission(selectedId, permKey)
      : await addRolePermission(selectedId, permKey);
    if (err) {
      // Rollback
      setPermissions(prev => {
        const next = new Set(prev);
        has ? next.add(permKey) : next.delete(permKey);
        return next;
      });
      setError(err.message);
    }
    setSavingPerm(null);
  };

  // ── Delete a role ──────────────────────────────────────────────────────────
  const handleDelete = async (role) => {
    if (role.is_system) return;
    if (!window.confirm(`Delete the "${role.name}" role? Users assigned this role will lose their permissions.`)) return;
    const { error: err } = await deleteFirmRole(role.id);
    if (err) { setError(err.message); return; }
    if (selectedId === role.id) setSelectedId(null);
    loadRoles();
  };

  const selectedRole = roles.find(r => r.id === selectedId);

  // ── Access guard ───────────────────────────────────────────────────────────
  if (!permLoading && !can('roles.manage')) return <AccessDenied />;

  return (
    <div className="flex h-full min-h-0 gap-0">

      {/* ── Left panel: Role list ─────────────────────────────────────────── */}
      <div className="w-64 flex-shrink-0 border-r border-slate-200 dark:border-slate-800 flex flex-col">
        <div className="px-4 py-4 border-b border-slate-200 dark:border-slate-800 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <ShieldCheck size={16} className="text-indigo-500" />
            <span className="font-semibold text-sm text-slate-800 dark:text-white">Roles</span>
          </div>
          <button
            onClick={() => setShowCreate(true)}
            className="p-1.5 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white transition"
            title="Create role"
          >
            <Plus size={14} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto py-2">
          {loadingRoles ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 size={20} className="animate-spin text-slate-400" />
            </div>
          ) : roles.length === 0 ? (
            <div className="px-4 py-6 text-center space-y-3">
              <p className="text-xs text-slate-500 dark:text-slate-400">
                No roles set up yet. Seed the standard ones (Admin, Partner, Attorney, Associate, Paralegal, Clerk, IT) and tweak from there.
              </p>
              <button
                onClick={handleSeedDefaults}
                disabled={seeding}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-medium disabled:opacity-50 transition"
              >
                {seeding && <Loader2 size={12} className="animate-spin" />}
                {seeding ? 'Seeding…' : 'Seed default roles'}
              </button>
            </div>
          ) : roles.map(role => (
            <button
              key={role.id}
              onClick={() => setSelectedId(role.id)}
              className={`w-full flex items-center gap-3 px-4 py-3 text-left transition group
                ${selectedId === role.id
                  ? 'bg-indigo-50 dark:bg-indigo-900/20 text-indigo-700 dark:text-indigo-300'
                  : 'text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800/50'}`}
            >
              <span
                className="w-2.5 h-2.5 rounded-full flex-shrink-0"
                style={{ background: role.color || '#6366F1' }}
              />
              <span className="flex-1 text-sm font-medium truncate">{role.name}</span>
              {role.is_system && (
                <Lock size={11} className="text-slate-400 flex-shrink-0" title="System role" />
              )}
              {selectedId === role.id && (
                <ChevronRight size={14} className="text-indigo-500 flex-shrink-0" />
              )}
            </button>
          ))}
        </div>
      </div>

      {/* ── Right panel: Permissions ──────────────────────────────────────── */}
      <div className="flex-1 overflow-y-auto p-6">
        {error && (
          <div className="flex items-center gap-2 bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300 text-sm rounded-xl px-4 py-3 mb-4 border border-red-200 dark:border-red-800">
            <AlertCircle size={14} className="flex-shrink-0" />
            {error}
          </div>
        )}

        {!selectedRole ? (
          <div className="flex flex-col items-center justify-center py-24 gap-3 text-center max-w-md mx-auto">
            <ShieldCheck size={32} className="text-slate-300 dark:text-slate-600" />
            {roles.length === 0 ? (
              <>
                <p className="text-slate-500 dark:text-slate-400 text-sm">
                  No roles configured for your firm yet. Roles control what each teammate can see and do — Partners can usually do everything; Paralegals see matters but not financials; etc.
                </p>
                <button
                  onClick={handleSeedDefaults}
                  disabled={seeding}
                  className="mt-2 inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-medium disabled:opacity-50 transition"
                >
                  {seeding && <Loader2 size={14} className="animate-spin" />}
                  {seeding ? 'Seeding…' : 'Seed default roles'}
                </button>
                <p className="text-xs text-slate-400 dark:text-slate-500 mt-2">
                  Creates Admin, Partner, Attorney, Associate, Paralegal, Clerk & IT with sensible permissions. Edit or delete any of them afterwards.
                </p>
              </>
            ) : (
              <p className="text-slate-500 dark:text-slate-400 text-sm">
                Select a role from the left to manage its permissions.
              </p>
            )}
          </div>
        ) : (
          <>
            {/* Role header */}
            <div className="flex items-start justify-between mb-6">
              <div className="flex items-center gap-3">
                <span
                  className="w-4 h-4 rounded-full flex-shrink-0 mt-0.5"
                  style={{ background: selectedRole.color || '#6366F1' }}
                />
                <div>
                  <div className="flex items-center gap-2">
                    <h2 className="text-xl font-bold text-slate-900 dark:text-white">
                      {selectedRole.name}
                    </h2>
                    {selectedRole.is_system && (
                      <span className="flex items-center gap-1 text-xs text-slate-500 dark:text-slate-400 bg-slate-100 dark:bg-slate-800 px-2 py-0.5 rounded-full">
                        <Lock size={10} /> System role
                      </span>
                    )}
                  </div>
                  {selectedRole.description && (
                    <p className="text-sm text-slate-500 dark:text-slate-400 mt-0.5">
                      {selectedRole.description}
                    </p>
                  )}
                </div>
              </div>
              {!selectedRole.is_system && (
                <button
                  onClick={() => handleDelete(selectedRole)}
                  className="flex items-center gap-1.5 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 px-3 py-1.5 rounded-lg transition"
                >
                  <Trash2 size={14} /> Delete role
                </button>
              )}
            </div>

            {selectedRole.is_system && selectedRole.name === 'Admin' && (
              <div className="flex items-start gap-2 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 text-amber-700 dark:text-amber-300 text-sm rounded-xl px-4 py-3 mb-6">
                <AlertCircle size={14} className="flex-shrink-0 mt-0.5" />
                Admin has all permissions and cannot be restricted.
              </div>
            )}

            {/* Permission toggles */}
            {loadingPerms ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 size={20} className="animate-spin text-slate-400" />
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {PERMISSION_GROUPS.map(group => (
                  <div key={group.label}>
                    <h3 className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-2 px-2">
                      {group.label}
                    </h3>
                    <div className="bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800 overflow-hidden">
                      {group.permissions.map(perm => (
                        <PermissionRow
                          key={perm.key}
                          perm={perm}
                          checked={permissions.has(perm.key)}
                          onChange={togglePermission}
                          saving={savingPerm === perm.key}
                          isSystemAdmin={selectedRole.name === 'Admin'}
                        />
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </div>

      {showCreate && (
        <CreateRoleModal
          firmId={firmId}
          onClose={() => setShowCreate(false)}
          onCreated={(newRole) => {
            setShowCreate(false);
            loadRoles();
            setSelectedId(newRole.id);
          }}
        />
      )}
    </div>
  );
}

export default function RolesPage() {
  return <PlanGate feature="roles"><RolesPageInner /></PlanGate>;
}
